/**
 * useBwiAggregate Hook
 * ====================
 * Fetches the cross-visit Behaviour Wellness & Impact aggregate and
 * per-visit comparison data for a patient (internal web form pipeline).
 */
import { useState, useCallback, useEffect } from 'react';

export interface BwiVisitEntry {
  visit_id: number;
  session_id: string | null;
  visit_date: string | null;
  overall_score: number;
  overall_label: string;
  emotional_distress: number;
  symptom_burden: number;
  affect_observed: number;
  risk_review: number;
  method: string;
  confidence: string;
  key_drivers: string[];
}

export interface BwiAggregate {
  agg_id: number;
  patient_id: number;
  visit_count: number;
  avg_overall_score: number;
  avg_emotional_distress: number;
  avg_symptom_burden: number;
  avg_affect_observed: number;
  avg_risk_review: number;
  highest_overall_score: number | null;
  lowest_overall_score: number | null;
  trend_direction: 'improving' | 'worsening' | 'stable' | 'insufficient_data';
  last_visit_score: number | null;
  second_last_visit_score: number | null;
  computed_at: string;
}

export function useBwiAggregate(patientId: number | null) {
  const [aggregate, setAggregate] = useState<BwiAggregate | null>(null);
  const [comparison, setComparison] = useState<BwiVisitEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!patientId) { setAggregate(null); setComparison([]); return; }
    setLoading(true);
    setError(null);
    try {
      const [aggRes, cmpRes] = await Promise.all([
        fetch(`/api/patient-summary/patients/${patientId}/bwi/aggregate`),
        fetch(`/api/patient-summary/patients/${patientId}/bwi/comparison`),
      ]);
      if (aggRes.status === 404) {
        setAggregate(null);
        setComparison([]);
        return;
      }
      if (!aggRes.ok) throw new Error(`Aggregate fetch failed: ${aggRes.status}`);
      const [aggData, cmpData] = await Promise.all([aggRes.json(), cmpRes.json()]);
      setAggregate(aggData);
      setComparison(cmpData.visits ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const recompute = useCallback(async () => {
    if (!patientId) return;
    setRecomputing(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/patient-summary/patients/${patientId}/bwi/aggregate/recompute`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(`Recompute failed: ${res.status}`);
      await fetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setRecomputing(false);
    }
  }, [patientId, fetchAll]);

  return { aggregate, comparison, loading, recomputing, error, refetch: fetchAll, recompute };
}
